import { FC, startTransition, useCallback, useMemo } from 'react';

import CurrentLocationXs from '@/assets/icons/CurrentLocationXs';
import { useHasPermission } from '@/auth';
import { defectIsBinnenPeriodeNietInzetbaar, defectTypeNietInzetbaar } from '@/domain/defecten/defect';
import { Defect } from '@/types/Defecten';
import { LocatieVoorEenheden } from '@/types/LocatieVoorEenheden';
import { classNames } from '@/utils/classNames';
import { dagEnMaandEnJaarDatum, tijdstipDatum } from '@/utils/dateUtils';

import usePlankDragSource from '../../hooks/usePlankDragSource';
import usePlankTableStore from '../../hooks/usePlankTableStore';
import useSidepanelActions from '../../hooks/useSidepanelActions';
import { defectToSelectionItem } from '../../utils/plankSelectionItems';
import NotitieRelatedItem from '../Notitie/NotitieRelatedItem';
import SidepanelToggle from '../Sidepanel/Toggle/SidepanelToggle';

import DefectIcon from './DefectIcon';

import plankStyles from '../../PlankView.module.css';
import tableStyles from '../Table/Table.module.css';
import defectStyles from './Defect.module.css';

export const titleVoorLocatie = (locatieVoorEenheden: LocatieVoorEenheden) => {
  const date = new Date(locatieVoorEenheden.tijd);
  if (!locatieVoorEenheden.stilstaande) {
    return `Locatie om ${tijdstipDatum(date)}`;
  }
  return `Locatie op ${dagEnMaandEnJaarDatum(date)} ${tijdstipDatum(date)}`;
};

const DefectListItem: FC<{ defect: Defect; isOnderhoudHerstel?: boolean }> = ({ defect, isOnderhoudHerstel }) => {
  const canMutate = useHasPermission('ddp.mutate');
  const { setEenheid, setTab } = useSidepanelActions();
  const isNietInzetbaar = defectTypeNietInzetbaar(defect);

  const byStel = usePlankTableStore((s) => s.defectenByStel);
  const defectsForUnit = useMemo<Defect[]>(
    () => byStel?.[String(defect.eenheidNummer)] ?? (defect ? [defect] : []),
    [byStel, defect, defect?.eenheidNummer],
  );

  const totalForUnit = defectsForUnit.length;
  const extra = Math.max(totalForUnit - 1, 0);

  const nietMeerInzetbaarVanaf = usePlankTableStore(
    (state) => state.defectNietInzetbaarVanaf?.[defect.eenheidNummer] ?? null,
  );

  const relatedNotitie = usePlankTableStore((state) => state.getDefectNotitie(defect));
  const canDragToOnderhoud = isNietInzetbaar && relatedNotitie?.status !== 'NOTITIE_OP_SB_OB' && canMutate;

  const selectionItem = useMemo(() => defectToSelectionItem(defect), [defect]);
  // Payload for the "sleep defect naar onderhoud" dropzone in TableRow, within the Plank. The
  // materieelSoort is written as a *key* because only the keys, not the values, are readable
  // from onDragEnter/onDragOver, where the dropzone decides whether it accepts this defect.
  const writeOnderhoudDragData = useCallback(
    (dataTransfer: DataTransfer) => {
      dataTransfer.setData('defect', JSON.stringify(defect));
      dataTransfer.setData(defect.materieelSoort, defect.materieelSoort);
      if (relatedNotitie?.status || relatedNotitie?.opmerking) {
        dataTransfer.setData('notitie', JSON.stringify(relatedNotitie));
      }
    },
    [defect, relatedNotitie],
  );

  // Whether this row is currently open in the sidepanel (shown with a focus border). The drag
  // cursor should also appear here, not only on a Ctrl/Shift multi-selection.
  const isFocused = usePlankTableStore((state) =>
    state.isEenheidSelected({ eenheid: defect.eenheidNummer, defect, isOnderhoud: isOnderhoudHerstel }),
  );

  const { isSelected, showDragCursor, dragProps, onSelectionClickCapture } = usePlankDragSource({
    item: selectionItem,
    testId: `draggableDefect_${defect.werkorderId ?? defect.eenheidNummer}`,
    writeInPlankDragData: canDragToOnderhoud ? writeOnderhoudDragData : undefined,
    isFocused,
  });

  const openStaatForEenheid = () => {
    startTransition(() => {
      void setEenheid({
        eenheid: defect.eenheidNummer,
        defect,
        notitie: relatedNotitie,
        isOnderhoud: isOnderhoudHerstel,
      });
      setTab?.('Staat');
    });
    setTimeout(() => {
      document.getElementById('ddp-sidepanel')?.focus();
    }, 100);
  };

  return (
    <SidepanelToggle
      eenheid={defect.eenheidNummer}
      defect={defect}
      notitie={relatedNotitie}
      isOnderhoud={isOnderhoudHerstel}
      onClickCapture={onSelectionClickCapture}
    >
      <div
        className={classNames(
          tableStyles.cardStyleXl,
          isSelected && tableStyles.plankSelectedStyle,
          showDragCursor && tableStyles.plankDraggableStyle,
        )}
        {...dragProps}
      >
        <div className={defectStyles.defectItemStyle}>
          <div className={defectStyles.tableListItemStyle}>
            <div className={defectStyles.tableListItemChildrenStyle}>
              <div className={defectStyles.tableListItemChildrenSpaceBetween}>
                <div className={plankStyles.eenheidStyle}>{defect.eenheidNummer}</div>
                <DefectIcon
                  defectSoort={defectTypeNietInzetbaar(defect) ? 'DEFECT_NIET_INZETBAAR' : defect.soort}
                  nietInzetbaarBinnen24Uur={defectIsBinnenPeriodeNietInzetbaar({
                    ...defect,
                    nietMeerInzetbaarVanaf,
                  })}
                />
                {defect.huidigeLocatie ? (
                  <div
                    data-testid={`defect_locatie_${defect.eenheidNummer}`}
                    className={plankStyles.locatieStyle}
                    title={titleVoorLocatie(defect.huidigeLocatie)}
                  >
                    {defect.huidigeLocatie.dienstregelpunt}
                    {!defect.huidigeLocatie.stilstaande && (
                      <CurrentLocationXs
                        className={plankStyles.locatieIconStyle}
                        width='16px'
                        height='16px'
                        aria-hidden='true'
                      />
                    )}
                  </div>
                ) : (
                  ''
                )}
                {extra > 0 && (
                  <button
                    className={defectStyles.extraDefectsButton}
                    aria-label='extra-defects-count'
                    title={`+${extra} extra defect(en) voor eenheid ${defect.eenheidNummer}`}
                    tabIndex={0}
                    draggable={false}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openStaatForEenheid();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        openStaatForEenheid();
                      }
                    }}
                  >
                    +{extra}
                  </button>
                )}
              </div>
            </div>
            <div>
              <NotitieRelatedItem notitie={relatedNotitie} />
            </div>
          </div>
          <div className={plankStyles.defectToelichtingContainer}>
            <div className={`${plankStyles.toelichtingStyle} ${plankStyles.defectToelichtingText}`}>
              {defect.toelichting}
            </div>
            {defect?.snelheidBeperking && (
              <span className={plankStyles.defectSnelheidBeperking}>
                {'Snelheid: '}
                <span className={plankStyles.defectStandaardSnelheid}>{defect?.standaardSnelheid}</span>
                {` ${defect?.snelheidBeperking}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </SidepanelToggle>
  );
};

export default DefectListItem;
